/**
 * Cache invalidation bus.
 *
 * Centralised place where:
 *   - SSE `ChangeEvent`s are translated into cache key / prefix invalidations
 *     (push-based invalidation), and
 *   - `useCachedQuery` consumers subscribe to those invalidations so a cached
 *     query can re-run as soon as the underlying data is known to have changed.
 *
 * The bus is intentionally framework-free: it's a plain module-level pub/sub.
 * A small bridge component (mounted at app root) wires `NotificationContext`
 * events into `processChangeEvent` below.
 */

import { ChangeEvent, ChangeEventTypes } from '../notificationService';
import {
  cacheInvalidate,
  cacheInvalidateByPrefix,
} from './localCacheService';
import { matchesCachePrefix } from './cacheKeys';

/**
 * An invalidation notice describes a single cache key (exact) or a prefix
 * (matches all keys starting with it).
 */
export interface CacheInvalidation {
  /** Exact key to invalidate. Mutually exclusive with `prefix`. */
  key?: string;
  /** Key prefix to invalidate. Mutually exclusive with `key`. */
  prefix?: string;
}

type Listener = (notice: CacheInvalidation) => void;

const listeners = new Set<Listener>();

/**
 * Subscribes to invalidation notices. Returns an unsubscribe function.
 * Used by `useCachedQuery` to revalidate when relevant data changes.
 */
export const subscribeToCacheInvalidations = (listener: Listener): (() => void) => {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
};

const notify = (notice: CacheInvalidation): void => {
  listeners.forEach(l => { try { l(notice); } catch { /* ignore */ } });
};

/**
 * Returns true if the given cache key is affected by the invalidation notice.
 * Used by `useCachedQuery` to decide whether to revalidate.
 */
export const isAffected = (cacheKey: string, notice: CacheInvalidation): boolean => {
  if (notice.key) return cacheKey === notice.key;
  if (notice.prefix) return matchesCachePrefix(cacheKey, notice.prefix);
  return false;
};

/**
 * Invalidates a single cache entry and notifies listeners.
 */
export const invalidateKey = async (key: string): Promise<void> => {
  await cacheInvalidate(key);
  notify({ key });
};

/**
 * Invalidates all cache entries matching the prefix and notifies listeners.
 */
export const invalidatePrefix = async (prefix: string): Promise<void> => {
  await cacheInvalidateByPrefix(prefix);
  notify({ prefix });
};

/**
 * Translates an SSE `ChangeEvent` into the cache invalidations it should trigger.
 * Called by the `CacheBusBridge` component, which is mounted at app root and
 * subscribes to `NotificationContext`.
 */
export const processChangeEvent = (event: ChangeEvent): void => {
  switch (event.type) {
    case ChangeEventTypes.AlbumCreated:
    case ChangeEventTypes.AlbumUpdated:
    case ChangeEventTypes.AlbumDeleted:
      // List endpoints
      void invalidatePrefix('albums');
      void invalidatePrefix('user-albums');
      // Single-album endpoint
      if (event.albumId) {
        void invalidateKey(`album:${event.albumId}`);
        // Visibility/contents changes can affect which photos a viewer sees
        // and the SAS URLs embedded in them; drop the album-photos cache so
        // the next view forces a fresh fetch with new tokens.
        void invalidateKey(`album-photos:${event.albumId}`);
      }
      break;

    case ChangeEventTypes.PhotoAdded:
    case ChangeEventTypes.PhotoDeleted:
    case ChangeEventTypes.PhotoReordered:
    case ChangeEventTypes.PhotoUpdated:
      if (event.albumId) {
        void invalidateKey(`album-photos:${event.albumId}`);
      }
      void invalidatePrefix('photos');
      void invalidatePrefix('geo-photos');
      // A new/changed photo can change album cover thumbnails
      void invalidatePrefix('albums');
      void invalidatePrefix('user-albums');
      break;

    case ChangeEventTypes.CommentAdded:
    case ChangeEventTypes.CommentDeleted:
    case ChangeEventTypes.CommentPinned:
    case ChangeEventTypes.CommentUnpinned:
      if (event.albumId && event.photoId) {
        void invalidateKey(`comments:photo:${event.albumId}:${event.photoId}`);
      } else if (event.albumId) {
        void invalidateKey(`comments:album:${event.albumId}`);
      }
      void invalidatePrefix('social-events');
      break;

    case ChangeEventTypes.ReactionAdded:
    case ChangeEventTypes.ReactionRemoved:
      if (event.albumId && event.photoId) {
        void invalidateKey(`reactions:photo:${event.albumId}:${event.photoId}`);
      } else if (event.albumId) {
        void invalidateKey(`reactions:album:${event.albumId}`);
      }
      void invalidatePrefix('social-events');
      break;

    case ChangeEventTypes.FriendshipCreated:
    case ChangeEventTypes.FriendshipRemoved:
      void invalidatePrefix('friends');
      // Friend visibility affects which albums show on Home
      void invalidatePrefix('albums');
      void invalidatePrefix('user-albums');
      void invalidatePrefix('social-events');
      break;

    default:
      // Unknown event type \u2014 ignore
      break;
  }
};
